import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import useLiveUpdates from "@/lib/useLiveUpdates";
import MapPlanner from "@/components/MapPlanner";
import WeatherTidePanel from "@/components/WeatherTidePanel";
import AICopilot from "@/components/AICopilot";
import OperationalOutputs from "@/components/OperationalOutputs";
import MarineSafetyPanel from "@/components/MarineSafetyPanel";
import ApprovalAttention from "@/components/ApprovalAttention";
import SeverityBadge from "@/components/SeverityBadge";
import { ChevronLeft, Send, CheckCircle2, XCircle, Plus, Trash2, GripVertical, Radio, Crosshair } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { TESTIDS } from "@/constants/testIds";
import { useAppAuth } from "@/auth/AppAuthProvider";

export default function PortCallDetail() {
  const { hasCapability } = useAppAuth();
  const { id } = useParams();
  const [planner, setPlanner] = useState(null);
  const [maps, setMaps] = useState([]);
  const [personnelAssignDlg, setPersonnelAssignDlg] = useState(false);
  const [pickedPersonnel, setPickedPersonnel] = useState("");
  const [personnelCandidates, setPersonnelCandidates] = useState([]);
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [personnelRequest, setPersonnelRequest] = useState({ labor_role: "", gang_id: "", task: "assigned", dispatched_by: "" });
  const [liveEvent, setLiveEvent] = useState(null);
  const [acknowledgedSops, setAcknowledgedSops] = useState([]);
  const [overrideDialog, setOverrideDialog] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");

  const refresh = useCallback(async () => {
    const p = await api.planner(id);
    setPlanner(p);
    if (p?.terminal?.id) {
      const m = await api.list("maps", { terminal_id: p.terminal.id });
      setMaps(m);
    }
  }, [id]);
  useEffect(() => { refresh(); }, [id, refresh]);

  // Live updates: subscribe to port-call + terminal topics
  const topics = planner?.terminal?.id
    ? [`port_call:${id}`, `terminal:${planner.terminal.id}`]
    : [`port_call:${id}`];
  useLiveUpdates(topics, useCallback((event, payload) => {
    setLiveEvent({ event, payload, ts: Date.now() });
    if (["assignment_changed", "status_changed", "conditions_updated", "annotation_changed"].includes(event)) {
      refresh();
    }
  }, [refresh]));

  if (!planner) return <div className="text-slate-500 font-mono text-xs">Loading planner…</div>;
  const { port_call: pc, vessel, berth, terminal, ship_assets, projected_ship_assets, terminal_assets, personnel, assignments, conflicts, humpy_options = [] } = planner;
  const activeMap = maps.find(m => m.is_active);
  const blocking = conflicts.filter(c => c.severity === "blocking");

  const handleMapAssign = async (terminalAssetId, shipAssetId = null, position = null) => {
    try {
      const existing = assignments.find(a => a.type === "asset" && a.resource_id === terminalAssetId);
      if (existing && position) {
        await api.positionAssignment(existing.id, {
          planned_position_x: position.x, planned_position_y: position.y,
          placement_method: "map_drag",
          expected_revision: existing.revision,
          ...(shipAssetId ? { ship_asset_id: shipAssetId } : {}),
        });
        if (shipAssetId) {
          await api.autoPositionAssignment(existing.id);
          toast.success("Optimal asset position updated");
        } else {
          toast.success("Asset position updated");
        }
        return;
      }
      const created = await api.create("assignments", {
        port_call_id: id, type: "asset", resource_id: terminalAssetId, ship_asset_id: shipAssetId,
        planned_position_x: shipAssetId ? null : position?.x,
        planned_position_y: shipAssetId ? null : position?.y,
        positioning_method: shipAssetId ? null : "map_drag",
        start: pc.eta, end: pc.etd, task: "auto", status: "tentative",
      });
      if (shipAssetId) await api.autoPositionAssignment(created.id);
      toast.success(shipAssetId ? "Asset assigned and optimally positioned" : "Asset positioned for port call");
    } catch (e) { toast.error(e.response?.data?.detail || "Assign failed"); }
  };

  const autoPosition = async (assignmentId) => {
    try {
      await api.autoPositionAssignment(assignmentId);
      toast.success("Optimal position calculated");
      await refresh();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "No valid position found");
      return false;
    }
  };

  const positionAssignment = async (assignment, payload) => {
    try {
      await api.positionAssignment(assignment.id, payload);
      toast.success(payload.position_locked ? "Position saved and locked" : "Exact position saved");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Position could not be saved");
      throw e;
    }
  };

  const submitPC = async () => { await api.submit(id, pc.revision); toast.success("Submitted for approval"); };
  const approvePC = async (decision, comments = "", overrideBlocking = false) => {
    try {
      await api.approve(id, { port_call_id: id, decision, comments, expected_revision: pc.revision, acknowledged_sop_ids: acknowledgedSops, override_blocking_conflicts: overrideBlocking });
      toast.success(`Port call ${decision}`);
      setOverrideDialog(false);
      setOverrideReason("");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval failed");
    }
  };
  const removeAssign = async (assignment) => { await api.remove("assignments", assignment.id, assignment.revision); };
  const checkPersonnelCandidates = async () => {
    setCandidateLoading(true);
    try {
      const result = await api.personnelCandidates(id, personnelRequest);
      setPersonnelCandidates(result.candidates || []);
      setPickedPersonnel(current => result.candidates?.some(item => item.personnel_id === current) ? current : "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not check personnel");
    } finally {
      setCandidateLoading(false);
    }
  };
  const openPersonnelDialog = open => {
    setPersonnelAssignDlg(open);
    if (open) {
      setPickedPersonnel("");
      setTimeout(checkPersonnelCandidates, 0);
    }
  };
  const addPersonnel = async () => {
    if (!pickedPersonnel) return;
    const candidate = personnelCandidates.find(item => item.personnel_id === pickedPersonnel);
    const proposed = candidate?.proposed_assignment || {};
    try {
      await api.create("assignments", {
        port_call_id: id, type: "personnel", resource_id: pickedPersonnel,
        start: proposed.start || pc.eta, end: proposed.end || pc.etd,
        task: proposed.task || personnelRequest.task || "assigned",
        labor_role: proposed.labor_role || personnelRequest.labor_role || null,
        gang_id: proposed.gang_id || personnelRequest.gang_id || null,
        dispatched_by: proposed.dispatched_by || personnelRequest.dispatched_by || null,
        status: "tentative",
      });
      setPersonnelAssignDlg(false); setPickedPersonnel(""); setPersonnelCandidates([]);
      toast.success(candidate?.eligible === false ? "Draft assignment added with issues to resolve" : "Personnel assigned");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Personnel assignment failed");
    }
  };

  return (
    <div className="space-y-4">
      {liveEvent && (Date.now() - liveEvent.ts < 4000) && (
        <div className="fixed bottom-4 right-4 z-50 panel px-3 py-2 text-xs font-mono flex items-center gap-2" data-testid="live-event-toast">
          <Radio size={12} className="text-emerald-400 animate-pulse" />
          <span className="text-slate-300">{liveEvent.event}</span>
          <span className="text-slate-500">· live</span>
        </div>
      )}
      <div className="flex items-center gap-3">
        <Link to="/port-calls" data-testid="back-to-port-calls" className="text-slate-400 hover:text-slate-100"><ChevronLeft size={18} /></Link>
        <div className="flex-1">
          <div className="font-mono text-[11px] uppercase tracking-widest text-slate-500 flex items-center gap-2">
            Port Call · {pc.id.slice(0, 8)}
            <span className="inline-flex items-center gap-1 text-emerald-400" title="Real-time updates connected" data-testid="live-indicator">
              <Radio size={9} /> LIVE
            </span>
          </div>
          <h1 className="font-heading text-2xl md:text-3xl font-black tracking-tighter text-slate-50">{vessel.name} <span className="text-slate-500 font-light">→</span> {berth?.name || "Unassigned"}</h1>
          <div className="font-mono text-xs text-slate-500">
            {terminal?.name} · ETA <span className="text-slate-300">{pc.eta?.slice(0, 16).replace("T", " ")}</span> · ETD <span className="text-slate-300">{pc.etd?.slice(0, 16).replace("T", " ")}</span> · {pc.side_to_quay}-to-quay
          </div>
          <Link to="/help#port-call-planning" className="text-xs text-sky-300 hover:text-sky-200">Port-call planning guide</Link>
        </div>
        <span className={`font-mono text-[10px] uppercase px-2 py-1 tracking-widest ${pc.status === "approved" ? "sev-ok" : pc.status === "submitted" ? "sev-info" : pc.status === "rejected" ? "sev-critical" : "sev-warning"}`}>{pc.status}</span>
        {pc.status === "draft" && hasCapability("plan.submit") && (
          <Button data-testid={TESTIDS.btnSubmitPortCall} size="sm" onClick={submitPC} className="bg-sky-600 hover:bg-sky-500 rounded-none">
            <Send size={14} className="mr-1.5" /> Submit
          </Button>
        )}
        {pc.status === "submitted" && hasCapability("plan.review") && (
          <div className="flex items-center gap-2">
            {blocking.length > 0 && (
              <span className="sev-blocking px-2 py-1 font-mono text-[10px]" data-testid="approval-blocked-badge">
                {blocking.length} blocking
              </span>
            )}
            <Button data-testid="btn-reject-port-call" size="sm" onClick={() => approvePC("rejected")} className="bg-red-900/30 hover:bg-red-800/50 border border-red-500/40 rounded-none">
              <XCircle size={14} className="mr-1.5" /> Reject
            </Button>
            <Button data-testid={TESTIDS.btnApprovePortCall} size="sm" onClick={() => blocking.length ? setOverrideDialog(true) : approvePC("approved")} className="bg-emerald-600 hover:bg-emerald-500 rounded-none">
              <CheckCircle2 size={14} className="mr-1.5" /> {blocking.length ? "Review & approve" : "Approve"}
            </Button>
          </div>
        )}
      </div>
      <ApprovalAttention attention={planner.approval_attention} />
      <Dialog open={overrideDialog} onOpenChange={setOverrideDialog}>
        <DialogContent className="bg-[#0E1522] border border-red-500/40 rounded-none text-slate-100 max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Approve with manual override</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-300">This call has {blocking.length} blocking conflict(s). Confirm that you reviewed them and record why approval may proceed.</div>
          <div className="max-h-40 overflow-y-auto space-y-1">
            {blocking.map(item => <div key={item.code} className="panel-2 p-2 text-xs"><span className="font-mono text-red-300">{item.code}</span> · {item.message}</div>)}
          </div>
          <div>
            <label className="text-xs text-slate-400" htmlFor="blocking-override-reason">Required override reason</label>
            <textarea id="blocking-override-reason" data-testid="blocking-override-reason" value={overrideReason} onChange={event => setOverrideReason(event.target.value)} rows={4} className="mt-1 w-full bg-[#050B14] border border-[#1E293B] p-2 text-sm" placeholder="Describe the verification, operational decision, and responsible approver." />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOverrideDialog(false)} className="rounded-none">Cancel</Button>
            <Button data-testid="confirm-blocking-override" disabled={!overrideReason.trim()} onClick={() => approvePC("approved", overrideReason.trim(), true)} className="bg-red-700 hover:bg-red-600 rounded-none disabled:opacity-40">Approve with override</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <section className="space-y-2" data-testid="port-call-map-primary">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-sky-400 font-mono">Primary planning workspace</div>
            <h2 className="font-heading text-lg font-bold text-slate-100">Terminal operations map</h2>
          </div>
          <div className="text-[11px] text-slate-500 font-mono">Place and inspect equipment here · use Full screen for detailed planning</div>
        </div>
        <MapPlanner
          planner={planner}
          mapFile={activeMap}
          terminalId={terminal?.id}
          onAssign={hasCapability("plan.edit") ? handleMapAssign : null}
          onPosition={hasCapability("plan.edit") ? positionAssignment : null}
          onAutoPosition={hasCapability("plan.edit") ? autoPosition : null}
        />
      </section>

      <MarineSafetyPanel analysis={planner.marine_safety} />
      <OperationalOutputs planner={planner} />

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
        <div className="xl:col-span-3 space-y-4">
          <div className="panel p-4" data-testid="conflicts-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Detected Conflicts · {conflicts.length}</div>
            </div>
            {conflicts.length === 0 && <div className="text-emerald-400 text-sm font-mono">No conflicts detected.</div>}
            <div className="space-y-2">
              {conflicts.map((c, i) => (
                <div key={i} className="flex items-start gap-3 panel-2 p-2.5" data-testid={`conflict-${c.code}`}>
                  <SeverityBadge severity={c.severity} compact />
                  <div className="flex-1">
                    <div className="text-sm">{c.message}</div>
                    <div className="font-mono text-[10px] text-slate-500">{c.code} · {c.category}</div>
                    {c.personnel_id && (
                      <Link to={`/personnel?person=${encodeURIComponent(c.personnel_id)}`} className="text-[11px] text-sky-300 hover:text-sky-200">
                        Worker: {personnel.find(person => person.id === c.personnel_id)?.name || c.personnel_id} · open labour profile
                      </Link>
                    )}
                    {(c.required_actions || []).map(action => <div key={action} className="text-xs text-amber-300 mt-1">Required: {action}</div>)}
                    {c.acknowledgement_required && c.sop_rule_id && (
                      <label className="text-xs mt-1 inline-flex items-center gap-2 text-sky-300">
                        <input type="checkbox" checked={acknowledgedSops.includes(c.sop_rule_id)} onChange={(event) => setAcknowledgedSops(event.target.checked ? [...new Set([...acknowledgedSops, c.sop_rule_id])] : acknowledgedSops.filter(item => item !== c.sop_rule_id))} />
                        Acknowledge this SOP before approval
                      </label>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="panel p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Assignments</div>
                <div className="font-heading text-sm">{assignments.length} active</div>
              </div>
              <Dialog open={personnelAssignDlg} onOpenChange={openPersonnelDialog}>
                <DialogTrigger asChild>
                  <Button data-testid={TESTIDS.btnAssignPersonnel} size="sm" className="bg-[#162032] hover:bg-[#1E2B41] border border-[#1E293B] rounded-none text-slate-200">
                    <Plus size={14} className="mr-1.5" /> Assign Personnel
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0E1522] border border-[#1E293B] rounded-none text-slate-100 max-w-xl">
                  <DialogHeader><DialogTitle className="font-heading">Assign personnel</DialogTitle></DialogHeader>
                  <p className="text-xs text-slate-400">Candidates are checked against their labour group, active rules, case history, availability, and current assignment window.</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Input aria-label="Labour role" placeholder="Labour role (optional)" value={personnelRequest.labor_role} onChange={event => setPersonnelRequest({...personnelRequest, labor_role: event.target.value})} className="bg-[#050B14] border-[#1E293B] rounded-none"/>
                    <Input aria-label="Gang" placeholder="Gang (optional)" value={personnelRequest.gang_id} onChange={event => setPersonnelRequest({...personnelRequest, gang_id: event.target.value})} className="bg-[#050B14] border-[#1E293B] rounded-none"/>
                  </div>
                  <details className="border border-[#1E293B] p-2 text-xs">
                    <summary className="cursor-pointer text-sky-300">Assignment details</summary>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <Input aria-label="Task" placeholder="Task" value={personnelRequest.task} onChange={event => setPersonnelRequest({...personnelRequest, task: event.target.value})} className="bg-[#050B14] border-[#1E293B] rounded-none"/>
                      <Input aria-label="Dispatched by" placeholder="Dispatched by" value={personnelRequest.dispatched_by} onChange={event => setPersonnelRequest({...personnelRequest, dispatched_by: event.target.value})} className="bg-[#050B14] border-[#1E293B] rounded-none"/>
                    </div>
                  </details>
                  <Button size="sm" variant="outline" onClick={checkPersonnelCandidates} disabled={candidateLoading} className="w-fit border-[#1E293B] rounded-none">
                    {candidateLoading ? "Checking…" : "Refresh candidates"}
                  </Button>
                  <Select value={pickedPersonnel} onValueChange={setPickedPersonnel}>
                    <SelectTrigger data-testid="select-personnel" className="bg-[#050B14] border-[#1E293B] rounded-none"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent className="bg-[#0E1522] border-[#1E293B] text-slate-100 rounded-none">
                      {personnelCandidates.map(candidate => <SelectItem key={candidate.personnel_id} value={candidate.personnel_id}>{candidate.name} · {candidate.role}{candidate.eligible ? "" : " · issues"}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {pickedPersonnel && (() => {
                    const candidate = personnelCandidates.find(item => item.personnel_id === pickedPersonnel);
                    return candidate ? <div className="border border-[#1E293B] p-3 text-xs">
                      <div className="flex justify-between gap-2"><span>{candidate.labor_group?.name || "Labour group not configured"}</span><span className={candidate.eligible ? "text-emerald-400" : "text-amber-300"}>{candidate.eligible ? "No worker-specific blockers" : "Review required"}</span></div>
                      {candidate.conflicts?.length > 0 && <details className="mt-2"><summary className="cursor-pointer text-amber-300">{candidate.conflicts.length} worker-specific issue(s)</summary><div className="mt-2 space-y-1">{candidate.conflicts.map((conflict, index) => <div key={`${conflict.code}-${index}`} className="text-slate-400"><span className="uppercase text-[10px] text-amber-300">{conflict.severity}</span> · {conflict.message}</div>)}</div></details>}
                      {!candidate.eligible && <p className="mt-2 text-slate-500">You may add this to the draft, but blocking issues must be manually resolved or explicitly overridden during approval.</p>}
                    </div> : null;
                  })()}
                  <DialogFooter><Button data-testid="btn-save-personnel-assign" disabled={!pickedPersonnel} onClick={addPersonnel} className="bg-sky-600 hover:bg-sky-500 rounded-none">Assign</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                  <th className="text-left pb-2">Type</th><th className="text-left pb-2">Resource</th><th className="text-left pb-2">Ship asset</th><th className="text-left pb-2">Window</th><th className="text-left pb-2">Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {assignments.map(a => {
                  const res = a.type === "asset"
                    ? terminal_assets.find(t => t.id === a.resource_id)
                    : personnel.find(p => p.id === a.resource_id);
                  const sa = ship_assets.find(s => s.id === a.ship_asset_id);
                  return (
                    <tr key={a.id} className="border-b border-[#1E293B] hover:bg-[#0E1522]" data-testid={`assignment-row-${a.id}`}>
                      <td className="font-mono py-2 text-slate-400">{a.type}</td>
                      <td className="py-2">
                        <div>{res?.name || a.resource_id}</div>
                        {a.positioning_explanation && <div className="font-mono text-[9px] text-slate-500">optimized from {a.positioning_explanation.candidates_considered} candidates · score {a.positioning_explanation.selected_score}</div>}
                      </td>
                      <td className="font-mono py-2 text-slate-500">{sa?.name || "—"}</td>
                      <td className="font-mono py-2 text-[11px]">{a.start?.slice(11, 16)} – {a.end?.slice(11, 16)}</td>
                      <td><span className="font-mono text-[10px] uppercase px-2 py-0.5 sev-info">{a.status}</span></td>
                      <td className="text-right">
                        {a.type === "asset" && a.ship_asset_id && <button title="Find optimal position" data-testid={`auto-position-${a.id}`} onClick={() => autoPosition(a.id)} className="text-slate-500 hover:text-sky-400 mr-3"><Crosshair size={12} /></button>}
                        <button data-testid={`del-assignment-${a.id}`} onClick={() => removeAssign(a)} className="text-slate-500 hover:text-red-400"><Trash2 size={12} /></button>
                      </td>
                    </tr>
                  );
                })}
                {assignments.length === 0 && <tr><td colSpan={6} className="text-slate-500 py-3 text-sm">No assignments yet. Drag a terminal asset onto a ship asset on the map.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-4">
          <WeatherTidePanel terminalId={terminal?.id} onUpdated={refresh} />

          {terminal_assets.some(asset => asset.type === "humpy") && (
            <div className="panel p-4" data-testid="humpy-suitability-panel">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">Humpy suitability</div>
              <div className="space-y-2">
                {humpy_options.map(option => {
                  const calculation = option.calculation || {};
                  const reason = calculation.issues?.[0]?.message;
                  return (
                    <div key={`${option.terminal_asset_id}-${option.ship_asset_id}`} className="panel-2 p-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm">{option.terminal_asset_name}</div>
                        <span className={`font-mono text-[10px] uppercase px-2 py-0.5 ${option.usable ? "sev-ok" : "sev-blocking"}`}>
                          {option.usable ? "usable" : "not usable"}
                        </span>
                      </div>
                      <div className="font-mono text-[10px] text-slate-500">{option.ship_asset_name}</div>
                      {calculation.low && calculation.high && (
                        <div className="font-mono text-[10px] text-slate-400 mt-1">
                          Required {calculation.low.required_height_relative_to_dock_m}m to {calculation.high.required_height_relative_to_dock_m}m vs dock
                        </div>
                      )}
                      {!option.usable && reason && <div className="text-[11px] text-red-300 mt-1">{reason}</div>}
                    </div>
                  );
                })}
                {humpy_options.length === 0 && (
                  <div className="text-xs text-amber-300">No compatible humpy/door combination can be evaluated. Check the ship-access compatibility settings.</div>
                )}
              </div>
            </div>
          )}

          <div className="panel p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">Available Terminal Assets · drag to map</div>
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {terminal_assets.filter(t => t.status === "available").map(t => (
                <div
                  key={t.id}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData("terminal_asset_id", t.id)}
                  data-testid={`drag-asset-${t.id}`}
                  className="flex items-center gap-2 panel-2 p-2 cursor-grab active:cursor-grabbing hover:bg-[#1E2B41] transition-colors"
                >
                  <GripVertical size={12} className="text-slate-500" />
                  <div className="flex-1">
                    <div className="text-sm">{t.name}</div>
                    <div className="font-mono text-[10px] text-slate-500">{t.type} · {t.capacity}</div>
                  </div>
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                </div>
              ))}
              {terminal_assets.filter(t => t.status === "available").length === 0 && <div className="text-xs text-slate-500">No available assets.</div>}
            </div>
          </div>

          <AICopilot portCallId={id} />
        </div>
      </div>
    </div>
  );
}
